import { safeGetItem, safeSetItem } from './safeStorage';

// Backend storage (backend/src/routes/uploads.ts) namespaces guest (non-logged-in)
// upload folders by the `X-Session-Id` header, falling back to the literal string
// 'guest' when it's absent — which every request from this frontend did until now,
// so every guest's uploaded images (and their derived variants: vectorized.svg,
// bg-removed.png, recolored-*.png, all keyed off the upload's own folder — see
// imageProcessing.ts's variantPath) landed in one shared folder and silently
// overwrote each other across completely unrelated browsers/sessions.
//
// Deliberately NOT reusing `customedge_design_hash` (Magento's PHP session hash,
// see useDesignerInit.ts): that's tied to one Magento session and absent entirely
// for any guest who didn't arrive via a `?hash=`-bearing launch URL. This needs to
// persist for as long as the browser might still reference images it uploaded, so
// it lives in localStorage (survives across tabs/sessions), generated once lazily.
const GUEST_SESSION_STORAGE_KEY = 'customedge_guest_session_id';

// `crypto.randomUUID()` is only defined in a "secure context" per the Web
// Crypto API spec (HTTPS, or localhost) — it's not merely restricted there,
// the function is entirely ABSENT from `crypto` outside one, so calling it
// throws "crypto.randomUUID is not a function" rather than a permission
// error. Confirmed live: this breaks specifically when the designer runs
// embedded in a Magento PDP iframe (see /m2-designer-pdp-cart-order's
// popup-embed section) whenever the surrounding Magento store — and
// therefore the iframed page's own origin — is served over plain HTTP, which
// is the norm for local/staging Magento installs (e.g. magento247.loc).
// `crypto.getRandomValues()`, unlike `randomUUID()`, IS available in every
// context (insecure ones too) since browsers shipped it years before the
// secure-context restriction existed — so it's the right primitive to build
// a UUID-shaped fallback from instead of reaching for a full polyfill
// library for one function.
export function generateUuidV4(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    // Last-resort fallback for an environment with no Web Crypto API at
    // all — Math.random() is not cryptographically strong, but this value
    // only ever needs to be a stable, collision-resistant-enough folder
    // name for one browser's guest uploads, not a security token.
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  // Force UUIDv4 version/variant bits per RFC 4122, matching what
  // crypto.randomUUID() itself produces.
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
}

export function getOrCreateGuestSessionId(): string {
  let id = safeGetItem('local', GUEST_SESSION_STORAGE_KEY);
  if (!id) {
    id = generateUuidV4();
    safeSetItem('local', GUEST_SESSION_STORAGE_KEY, id);
  }
  return id;
}
